import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { revenue, type RevenueGranularity } from "@/api/endpoints";
import { Money } from "@/components/shared/Money";
import { Spinner } from "@/components/shared/Spinner";
import { autoGranularity, RevenueChart } from "./RevenueChart";
import { defaultWindow, WindowPicker, type Window } from "./WindowPicker";

// Auto-refresh cadence — aligned with the backend's hourly earnings rollup at
// :15. We refetch every hour from page open; the user always sees data within
// at most an hour of the latest rollup pass.
const REVENUE_REFETCH_MS = 60 * 60 * 1000;

type SubTab = "deployment" | "gpu_type" | "contract" | "tenant";

const TABS: { key: SubTab; label: string }[] = [
  { key: "deployment", label: "By deployment" },
  { key: "gpu_type", label: "By GPU type" },
  { key: "contract", label: "By contract" },
  { key: "tenant", label: "By tenant" },
];

export function RevenueDashboard() {
  const [tab, setTab] = useState<SubTab>("deployment");
  const [window, setWindow] = useState<Window>(defaultWindow());
  // Granularity is shared between the chart and the CSV export so the export
  // breaks revenue down at whatever resolution the user is viewing.
  const [gran, setGran] = useState<RevenueGranularity | "auto">("daily");
  const effectiveGran: RevenueGranularity =
    gran === "auto" ? autoGranularity(window.start, window.end) : gran;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-lg font-semibold">Revenue</h1>
        <div className="flex items-center gap-3">
          <WindowPicker value={window} onChange={setWindow} />
          <ExportButton window={window} granularity={effectiveGran} />
        </div>
      </div>

      <div className="border-b border-slate-200 dark:border-slate-800">
        <nav className="-mb-px flex gap-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                tab === t.key
                  ? "border-b-2 border-brand-500 px-1 py-2 text-sm font-medium text-brand-700"
                  : "border-b-2 border-transparent px-1 py-2 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
              }
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <RevenueChart
        dimension={tab}
        start={window.start}
        end={window.end}
        gran={gran}
        onGranChange={setGran}
      />

      {tab === "deployment" && <ByDeployment window={window} />}
      {tab === "gpu_type" && <ByGpuType window={window} />}
      {tab === "contract" && <ByContract window={window} />}
      {tab === "tenant" && <ByTenant window={window} />}
    </div>
  );
}

function ExportButton({
  window,
  granularity,
}: {
  window: Window;
  granularity: RevenueGranularity;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    setBusy(true);
    setError(null);
    try {
      await revenue.exportDetailCsv(window.start, window.end, granularity);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end">
      <button
        onClick={onClick}
        disabled={busy}
        title={`Download per-server ${granularity} revenue breakdown (GPU type, deployment, tenant, contract) for the selected window`}
        className="rounded-md bg-brand-500 px-3 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
      >
        {busy ? "Exporting…" : "Export CSV"}
      </button>
      {error && <span className="mt-1 text-xs text-red-700">{error}</span>}
    </div>
  );
}

function variancePill(realized: number, expected: number): { text: string; tone: string } {
  if (expected <= 0) return { text: "—", tone: "text-slate-400" };
  const pct = ((realized - expected) / expected) * 100;
  const sign = pct >= 0 ? "+" : "";
  const tone =
    pct >= -5
      ? "text-emerald-700 dark:text-emerald-400"
      : pct >= -20
        ? "text-amber-700 dark:text-amber-400"
        : "text-red-700 dark:text-red-400";
  return { text: `${sign}${pct.toFixed(1)}%`, tone };
}

function ByDeployment({ window }: { window: Window }) {
  const q = useQuery({
    queryKey: ["revenue", "deployment", window.start, window.end],
    queryFn: () => revenue.byDeployment(window.start, window.end),
    refetchInterval: REVENUE_REFETCH_MS,
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <div className="text-red-700">{(q.error as Error).message}</div>;
  const rows = q.data ?? [];
  const total = rows.reduce((s, r) => s + Number(r.realized_revenue), 0);

  return (
    <div className="space-y-3">
      <Summary label={`${rows.length} deployments`} total={total} isFetching={q.isFetching} />
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">Deployment</th>
              <th className="px-3 py-2 text-left">GPU type</th>
              <th className="px-3 py-2 text-left">Financing</th>
              <th className="px-3 py-2 text-right">Devices</th>
              <th className="px-3 py-2 text-right">Realized</th>
              <th className="px-3 py-2 text-right">Projected (Tempo)</th>
              <th className="px-3 py-2 text-right">Δ</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                  No deployments. Assign devices to a Tempo deployment from the Inventory tab.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const realized = Number(r.realized_revenue);
              const projected = r.projected_revenue ? Number(r.projected_revenue) : 0;
              const v = variancePill(realized, projected);
              return (
                <tr
                  key={r.tempo_deployment_id ?? "unassigned"}
                  className="border-t border-slate-100 dark:border-slate-800"
                >
                  <td className="px-3 py-2 font-medium">
                    {r.tempo_deployment_id ? (
                      <Link
                        to={`/revenue/deployment/${r.tempo_deployment_id}?start=${window.start}&end=${window.end}`}
                        className="hover:underline"
                      >
                        {r.name}
                      </Link>
                    ) : (
                      <span className="text-slate-500">Unassigned</span>
                    )}
                    {r.is_archived && (
                      <span className="ml-2 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        archived
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.gpu_type_name ?? "—"}</td>
                  <td className="px-3 py-2">
                    {r.financing_type ? (
                      <FinancingPill type={r.financing_type} />
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{r.device_count}</td>
                  <td className="px-3 py-2 text-right">
                    <Money value={r.realized_revenue} muted={realized === 0} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.projected_revenue ? (
                      <Money value={r.projected_revenue} />
                    ) : r.tempo_deployment_id ? (
                      <span className="text-slate-400" title="Tempo simulation pending or unavailable">
                        —
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${v.tone}`}>{v.text}</td>
                  <td className="px-3 py-2 text-right">
                    {r.tempo_url && (
                      <a
                        href={r.tempo_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-brand-700 hover:underline"
                      >
                        Tempo ↗
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FinancingPill({ type }: { type: string }) {
  const tone =
    type === "loan"
      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
      : type === "lease"
        ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${tone}`}>{type}</span>
  );
}

function Summary({
  label,
  total,
  isFetching,
}: {
  label: string;
  total: number;
  isFetching?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3 text-sm text-slate-600 dark:text-slate-300">
      <span>{label}</span>
      {isFetching && (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
          Updating…
        </span>
      )}
      <span className="text-slate-300 dark:text-slate-600">·</span>
      <span>
        Total realized: <Money value={total} />
      </span>
    </div>
  );
}

function ByGpuType({ window }: { window: Window }) {
  const q = useQuery({
    queryKey: ["revenue", "gpu_type", window.start, window.end],
    queryFn: () => revenue.byGpuType(window.start, window.end),
    refetchInterval: REVENUE_REFETCH_MS,
  });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <div className="text-red-700">{(q.error as Error).message}</div>;
  const rows = q.data ?? [];
  const total = rows.reduce((s, r) => s + Number(r.realized_revenue), 0);

  return (
    <div className="space-y-3">
      <Summary label={`${rows.length} GPU types`} total={total} isFetching={q.isFetching} />
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">GPU type</th>
              <th className="px-3 py-2 text-right">Devices</th>
              <th className="px-3 py-2 text-right">Realized</th>
              <th className="px-3 py-2 text-right">Per device</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-400">
                  No data.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.gpu_type ?? "unknown"} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2 font-medium">
                  {r.gpu_type ?? <span className="text-slate-400">Unknown</span>}
                </td>
                <td className="px-3 py-2 text-right">{r.device_count}</td>
                <td className="px-3 py-2 text-right">
                  <Money value={r.realized_revenue} muted={Number(r.realized_revenue) === 0} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Money value={r.revenue_per_device} muted={Number(r.revenue_per_device) === 0} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ByContract({ window }: { window: Window }) {
  const q = useQuery({
    queryKey: ["revenue", "contract", window.start, window.end],
    queryFn: () => revenue.byContract(window.start, window.end),
    refetchInterval: REVENUE_REFETCH_MS,
  });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <div className="text-red-700">{(q.error as Error).message}</div>;
  const rows = q.data ?? [];
  const total = rows.reduce((s, r) => s + Number(r.realized_revenue), 0);

  return (
    <div className="space-y-3">
      <Summary label={`${rows.length} contracts`} total={total} isFetching={q.isFetching} />
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">Customer</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Term</th>
              <th className="px-3 py-2 text-right">Devices</th>
              <th className="px-3 py-2 text-right">Credits</th>
              <th className="px-3 py-2 text-right">Realized</th>
              <th className="px-3 py-2 text-right">Expected billing</th>
              <th className="px-3 py-2 text-right">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                  No active contracts. Add some in the Contracts tab.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const realized = Number(r.realized_revenue);
              const expected = Number(r.expected_billing_for_window);
              const credits = Number(r.credits_in_window);
              const v = variancePill(realized, expected);
              return (
                <tr key={r.contract_id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-3 py-2 font-medium">
                    <Link to={`/contracts/${r.contract_id}`} className="hover:underline">
                      {r.customer_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {r.start_date} → {r.end_date}
                  </td>
                  <td className="px-3 py-2 text-right">{r.device_count}</td>
                  <td className="px-3 py-2 text-right">
                    {credits > 0 ? (
                      <span className="text-red-700 dark:text-red-400">
                        <Money value={-credits} />
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={r.realized_revenue} muted={realized === 0} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={r.expected_billing_for_window} muted={expected === 0} />
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${v.tone}`}>{v.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ByTenant({ window }: { window: Window }) {
  const q = useQuery({
    queryKey: ["revenue", "tenant", window.start, window.end],
    queryFn: () => revenue.byTenant(window.start, window.end),
    refetchInterval: REVENUE_REFETCH_MS,
  });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <div className="text-red-700">{(q.error as Error).message}</div>;
  const rows = q.data ?? [];
  const total = rows.reduce((s, r) => s + Number(r.realized_revenue), 0);

  return (
    <div className="space-y-3">
      <Summary label={`${rows.length} tenants`} total={total} isFetching={q.isFetching} />
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">Tenant</th>
              <th className="px-3 py-2 text-right">Devices</th>
              <th className="px-3 py-2 text-right">Idle</th>
              <th className="px-3 py-2 text-right">Realized</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-400">
                  No data.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.tenant ?? "unassigned"} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2 font-medium">
                  {r.tenant ?? <span className="text-slate-400">Unassigned</span>}
                </td>
                <td className="px-3 py-2 text-right">{r.device_count}</td>
                <td className="px-3 py-2 text-right">
                  {r.idle_device_count > 0 ? (
                    <span className="text-amber-700 dark:text-amber-400">{r.idle_device_count}</span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <Money value={r.realized_revenue} muted={Number(r.realized_revenue) === 0} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
